// HighlightCard.tsx - VERSIÓN MEJORADA CON LÓGICA DE REINTENTOS
import React, { useRef } from "react";
import { Highlight } from "../../../../hooks";
import { usePublication } from "../../../../hooks/usePublication";
import { findTypeOption, typeLibro } from "../../../../interfaces/Publication";
import { useParams, Link } from "react-router-dom";
import { ConfirmDeleteButton } from "./ConfirmDeleteButton";

interface HighlightCardProps {
  highlight: Highlight;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export const HighlightCard: React.FC<HighlightCardProps> = ({
  highlight,
  onEdit,
  onDelete,
}) => {
  const { publication } = usePublication();
  let { idRevista, idLibro } = useParams();
  const typeLibro: typeLibro = idLibro ? "libro" : "revista";
  const route = findTypeOption(typeLibro)?.route || "";
  const lastClickTimeRef = useRef<number>(0);
  const scrollAttemptRef = useRef<number>(0);

  // Encontrar en qué sección está este fragmento
  const findSectionIndexForFragment = (fragmentId: string): number => {
    for (let i = 0; i < publication.sections.length; i++) {
      const section = publication.sections[i];
      const hasFragment = section.subsections?.some((subsection) =>
        subsection.fragments.some(
          (fragment) => fragment.id.toString() === fragmentId
        )
      );
      if (hasFragment) return i;
    }
    return 0;
  };

  // Determinar el tipo de fragmento (TXT, IMG, etc.)
  const getFragmentType = (fragmentId: string): string => {
    for (const section of publication.sections) {
      for (const subsection of section.subsections || []) {
        for (const fragment of subsection.fragments) {
          if (fragment.id.toString() === fragmentId) {
            return fragment.type;
          }
        }
      }
    }
    return "TXT"; // Fallback
  };

  const sectionIndex = findSectionIndexForFragment(highlight.fragmentId);
  const fragmentType = getFragmentType(highlight.fragmentId);
  const fragmentAnchor = `${fragmentType}${highlight.fragmentId}`;

  // Función para verificar si el mark del resaltado existe
  const checkIfHighlightExists = (): boolean => {
    const escapedHlId = CSS.escape(highlight.id);
    const mark = document.querySelector(`mark[data-hl-id="${escapedHlId}"]`);
    return !!mark;
  };

  // Función para intentar scroll al resaltado con reintentos
  const attemptHighlightScroll = (attempt: number = 0) => {
    const maxAttempts = 10;

    if (attempt >= maxAttempts) {

      return;
    }

    if (checkIfHighlightExists()) {

      window.dispatchEvent(
        new CustomEvent("scrollToAnchor", {
          detail: { anchorName: highlight.id },
        })
      );
    } else {

      setTimeout(() => {
        attemptHighlightScroll(attempt + 1);
      }, 500); // Reintentar cada 500ms
    }
  };

  const handleClick = (e: React.MouseEvent) => {

    // PREVENIR CLICS RÁPIDOS
    const now = Date.now();
    if (lastClickTimeRef.current && now - lastClickTimeRef.current < 1000) {
      return;
    }
    lastClickTimeRef.current = now;

    // Cerrar menú en móviles
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    if (isMobile) {
      const menu = document.querySelector(".js-nav");
      menu?.classList.remove("expand");
    }


    // 1. Primero navegar al fragmento
    // 2. Esperar a que el fragmento cargue Y los resaltados se apliquen
    // 3. Hacer scroll al resaltado específico con reintentos

    setTimeout(() => {
      // Paso 1: Navegar al fragmento
      window.dispatchEvent(
        new CustomEvent("scrollToAnchor", {
          detail: { anchorName: fragmentAnchor },
        })
      );

      // Paso 2: Esperar más tiempo para que el fragmento cargue completamente
      // y los resaltados se apliquen via applyHighlightsToContainer
      setTimeout(() => {
        attemptHighlightScroll(0);
      }, 1200); // Delay más largo para fragments pesados
    }, 100);
  };

  return (
    <div className="card">
      {/* Navegar al FRAGMENTO, no directamente al resaltado */}
      <Link
        to={`/${route}/${publication.id}/${sectionIndex}#${fragmentAnchor}`}
        onClick={handleClick}
      >
        <div className="card__header">
          <q className="card__quote">{highlight.text}</q>
          <div
            className="card__header-badge"
            style={{ backgroundColor: highlight.color }}
          ></div>
        </div>
      </Link>

      <div className="card__body">
        <div className="card__comment">
          {highlight.note && <p>{highlight.note}</p>}
        </div>
      </div>

      <div className="card__actions">
        <ConfirmDeleteButton onConfirm={() => onDelete(highlight.id)} />
        <button
          className="btn btn-alt-primary btn-edit tippy"
          data-tippy-content="Editar selección y nota"
          aria-label="Editar selección y nota"
          onClick={() => onEdit(highlight.id)}
        >
          <span className="btn__label">Editar</span>
        </button>
      </div>
    </div>
  );
};
